import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ship, HelpCircle, Image, Mail, TrendingUp, ArrowRight } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      
      setStats(statsRes.data);
      setRecentContacts(contactsRes.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Boat Models', 
      value: stats?.boats || 0, 
      icon: Ship, 
      color: 'bg-blue-500',
      link: '/admin/boats'
    },
    { 
      title: 'FAQ Items', 
      value: stats?.faq || 0, 
      icon: HelpCircle, 
      color: 'bg-green-500',
      link: '/admin/faq'
    },
    { 
      title: 'Gallery Images', 
      value: stats?.gallery || 0, 
      icon: Image, 
      color: 'bg-purple-500',
      link: '/admin/gallery'
    },
    { 
      title: 'Contact Inquiries', 
      value: stats?.contacts?.total || 0, 
      subtitle: `${stats?.contacts?.unread || 0} unread`,
      icon: Mail, 
      color: 'bg-orange-500',
      link: '/admin/contacts'
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="dashboard-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome to your admin panel</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <Link 
              key={stat.title}
              to={stat.link}
              className="bg-white p-6 border-2 border-slate-100 hover:border-orange-500/50 transition-colors"
              data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-500 text-sm uppercase tracking-wide">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-orange-500 text-sm mt-1">{stat.subtitle}</p>
                  )}
                </div>
                <div className={`${stat.color} p-3`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Recent Contacts */}
        <div className="bg-white border-2 border-slate-100">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-slate-900 uppercase">Recent Inquiries</h2>
            <Link to="/admin/contacts" className="text-orange-500 text-sm flex items-center gap-1 hover:gap-2 transition-all">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentContacts.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No contact inquiries yet
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentContacts.map((contact) => (
                <div key={contact.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {contact.firstName} {contact.lastName}
                        {!contact.read && (
                          <span className="ml-2 text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">New</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-500">{contact.email}</p>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-1">{contact.message}</p>
                    </div>
                    <p className="text-xs text-slate-400">
                      {new Date(contact.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
